import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { CONCEPT_TOURS } from '../data/consoleModules';
import { askNova } from '../components/ConsoleHeader';
import { useAuth, signOut } from '../AuthContext';
import { MarkdownLite } from './Plan';
import { AMBIENT_SCREENS } from '../components/ambient';
import { PORTFOLIO_SCREENS } from '../components/portfolio';
import { RCM_SCREENS } from '../components/rcm';
import './ConceptPage.css';

// Concept pages (/concepts/:page). Served from the Firestore
// `conceptSections` collection; the markdown master is docs/concepts/<page>.md,
// synced by scripts/sync-concept-page.mjs --page <page>. Two postures, both
// decided by the rules: GATED (allowlist, same as /docs — the default) and
// UNLISTED-PUBLIC (frontmatter `access: public` → rows carry `public: true`
// and anyone with the link reads them, signed out included; the route is
// not behind RequireAuth for that reason). Not linked from public nav;
// robots Disallow. One registry entry per page: the live `screen:<id>`
// figures it may embed. The NoVA tour for a page is CONCEPT_TOURS[page].
export const CONCEPT_PAGES = {
  'ambient-screen': { screens: AMBIENT_SCREENS },
  portfolio: { screens: PORTFOLIO_SCREENS },
  'revenue-cycle': { screens: RCM_SCREENS },
};

const FIG = /^!\[([^\]]*)\]\(([^)]*)\)\s*$/;
const CAP = /^\*(.+)\*$/;

// MarkdownLite has no image syntax, so split the body on figure lines:
// `![alt](src)` + an optional italic `*caption*` line become a <figure>, the
// text between them goes through MarkdownLite unchanged. A src of
// `screen:<id>` renders the live React figure registered for the page
// instead of an <img> (unknown ids render the caption only).
// `focus` = { fig, frame, view } from the URL (?fig=&frame=&view=) — the
// figure whose id matches gets the frame as a controlled prop, which is how
// NoVA drives a carousel during the guided tour (CONCEPT_TOURS).
const SCREEN = /^screen:(.+)$/;

export function MarkdownFigures({ text, focus, screens = AMBIENT_SCREENS }) {
  const lines = (text || '').split('\n');
  const out = [];
  let buf = [];
  const flush = () => {
    if (buf.join('').trim()) out.push(<MarkdownLite key={out.length} text={buf.join('\n')} />);
    buf = [];
  };
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].trim().match(FIG);
    if (!m) { buf.push(lines[i]); continue; }
    flush();
    const cap = (lines[i + 1] || '').trim().match(CAP);
    if (cap) i++;
    const sc = m[2].match(SCREEN);
    const Screen = sc ? screens[sc[1]] : null;
    const focused = sc && focus && focus.fig === sc[1];
    out.push(
      <figure className={`cs-concept-figure ${sc ? 'is-screen' : ''}`} key={out.length} id={sc ? `fig-${sc[1]}` : undefined}>
        {sc ? (Screen && <Screen frame={focused ? focus.frame : null} view={focused ? focus.view : undefined} />) : <img src={m[2]} alt={m[1]} loading="lazy" />}
        {cap && <figcaption><em>{cap[1]}</em></figcaption>}
      </figure>
    );
  }
  flush();
  return <>{out}</>;
}

// The page's rows. A member reads the whole gated collection (one listener
// serves every concept page); a visitor — or a member the rules refuse — gets
// the public rows of this page only (two equality filters, no composite
// index; sorted here). `mode` tells the access states apart from "loading".
function useConceptRows(page, member, authLoading) {
  const [state, setState] = useState({ docs: null, error: null, mode: null });
  const [fellBack, setFellBack] = useState(false);
  useEffect(() => { setFellBack(false); }, [page, member]);
  useEffect(() => {
    if (authLoading || !db) return undefined;
    const gated = member && !fellBack;
    setState({ docs: null, error: null, mode: gated ? 'gated' : 'public' });
    const col = db.collection('conceptSections');
    const q = gated ? col.orderBy('order', 'asc') : col.where('page', '==', page).where('public', '==', true);
    const unsub = q.onSnapshot(
      (snap) => setState({
        docs: snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((d) => d.page === page).sort((a, b) => a.order - b.order),
        error: null,
        mode: gated ? 'gated' : 'public',
      }),
      (err) => {
        if (gated && /permission/i.test(err.message)) { setFellBack(true); return; } // no seat → maybe the page is public
        setState({ docs: [], error: err.message, mode: gated ? 'gated' : 'public' });
      }
    );
    return () => unsub();
  }, [page, member, fellBack, authLoading]);
  return state;
}

// First visit to a page that has a tour: one card over the hero that makes
// the choice explicit — NoVA walks the page by voice (the click is the
// gesture a browser needs before it may play audio; talking back is the
// mic, typing works too) or they read. Remembered per page, per browser.
const arrivalKey = (page) => `nv:concept-arrival:${page}`;
function useArrival(page, hasTour) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    let seen = false;
    try { seen = !!localStorage.getItem(arrivalKey(page)); } catch { /* private mode */ }
    setShow(hasTour && !seen);
  }, [page, hasTour]);
  const done = (choice) => {
    try { localStorage.setItem(arrivalKey(page), `${choice}:${Date.now()}`); } catch { /* ignore */ }
    setShow(false);
  };
  return { show, done };
}

// Highlight the last section whose heading has scrolled past the nav line.
function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0] || '');
  useEffect(() => {
    if (!ids.length) return undefined;
    const update = () => {
      let cur = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 200) cur = id;
      }
      setActive(cur);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, [ids.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps
  return active;
}

export default function ConceptPage() {
  const { page: PAGE } = useParams();
  const def = CONCEPT_PAGES[PAGE];
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const member = !!user && !user.isAnonymous;
  const { docs, error, mode } = useConceptRows(PAGE, member, authLoading);
  const rows = docs || [];
  const header = rows.find((d) => d.order === 0) || null;
  const sections = rows.filter((d) => d.order > 0);
  const active = useActiveSection(sections.map((s) => s.id));

  // Deep link / tour target: ?sec=<sectionId> scrolls the section into view
  // (or the figure when ?fig= is set); ?frame= is 0-based.
  const [params] = useSearchParams();
  const sec = params.get('sec');
  const fig = params.get('fig');
  const frameParam = params.get('frame');
  const focus = fig ? { fig, frame: frameParam == null ? null : Math.max(0, Number(frameParam) || 0), view: params.get('view') || undefined } : null;
  const ready = !!header;
  useEffect(() => {
    if (!ready || (!sec && !fig)) return undefined;
    // Images above the target keep loading for a while and shift the layout
    // under a single scroll, so re-aim a few times (instant after the first).
    const go = (smooth) => {
      const el = (fig && document.getElementById(`fig-${fig}`)) || (sec && document.getElementById(sec));
      if (!el) return;
      // A figure is centred unless it is taller than the viewport (the
      // teaser sheet) — then its top goes to the top, or the head is cut off.
      const tall = fig && el.getBoundingClientRect().height > window.innerHeight * 0.8;
      el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: fig && !tall ? 'center' : 'start' });
    };
    const ts = [setTimeout(() => go(true), 80), setTimeout(() => go(false), 900), setTimeout(() => go(false), 2200)];
    return () => ts.forEach(clearTimeout);
  }, [ready, sec, fig, frameParam]);
  const tour = CONCEPT_TOURS[PAGE];
  const arrival = useArrival(PAGE, !!tour);

  if (!def) return <Navigate to="/" replace />;
  if (error) {
    return (
      <div className="cs-page">
        <div className="cs-empty" style={{ maxWidth: 520 }}>Couldn't load the page: {error}</div>
      </div>
    );
  }
  if (docs === null) {
    return (
      <div className="cs-page">
        <div className="cs-empty">Loading the concept…</div>
      </div>
    );
  }
  if (!header) {
    // No public rows for this page. A visitor is sent to sign in (the page
    // may be gated); a member who got here through the public query has a
    // seat but no grant — invite-only, say so.
    if (mode === 'public' && !member) {
      const next = location.pathname + location.search;
      return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
    }
    return (
      <div className="cs-page">
        <div className="cs-empty" style={{ maxWidth: 520 }}>
          {mode === 'public' && member ? (
            <>
              <p style={{ marginTop: 0 }}>
                <strong>{user?.email}</strong> isn't approved for this concept
                page. Access is invite-only — contact Nventr to have your email
                added.
              </p>
              <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => signOut()}>
                Sign out
              </button>
            </>
          ) : 'This concept page hasn\'t been published yet.'}
        </div>
      </div>
    );
  }

  return (
    <div className="cs-concept">
      <header className="cs-concept-hero">
        {header.hero && <img src={header.hero} alt={header.heroCaption || header.title} />}
        <div className="cs-concept-hero-text">
          <p className="cs-concept-kicker">{header.version}</p>
          <h1>{header.title}</h1>
          {header.subtitle && <p className="cs-concept-subtitle">{header.subtitle}</p>}
          {tour && (
            <div className="cs-concept-tour-row">
              <button type="button" className="cs-btn cs-btn-primary cs-concept-tour-btn" onClick={() => { arrival.done('voice'); askNova(tour.prompt); }}>
                ◈ Walk me through it with NoVA
              </button>
              <span>About six minutes by voice · interrupt, ask or type at any point</span>
            </div>
          )}
        </div>
        {tour && arrival.show && (
          <div className="cs-concept-arrival" role="dialog" aria-labelledby="cs-concept-arrival-title">
            <div className="cs-concept-arrival-card">
              <p className="cs-concept-kicker">A guided page</p>
              <h2 id="cs-concept-arrival-title">This page walks itself.</h2>
              <p>
                NoVA, the agent on every nventr screen, narrates it in about six
                minutes — the sheet first, band by band, then the figures behind
                it. Interrupt, ask a question or type at any point; it answers and
                picks the walk back up.
              </p>
              <div className="cs-concept-arrival-actions">
                <button type="button" className="cs-btn cs-btn-primary" onClick={() => { arrival.done('voice'); askNova(tour.prompt); }}>
                  ◈ Start the walkthrough
                </button>
                <button type="button" className="cs-btn cs-btn-outline" onClick={() => arrival.done('read')}>
                  Read it myself
                </button>
              </div>
              <small>
                Sound on. Your browser will ask for the microphone so you can talk
                back — decline it and you can still type. The button at the top of
                the page starts it later.
              </small>
            </div>
          </div>
        )}
      </header>
      {header.heroCaption && (
        <p className="cs-concept-hero-caption"><em>{header.heroCaption}</em></p>
      )}

      <div className="cs-concept-body">
        <nav className="cs-concept-rail" aria-label="Contents">
          <p className="cs-concept-rail-label">Contents</p>
          {sections.map((s) => (
            <a key={s.id} href={`#${s.id}`} className={s.id === active ? 'is-active' : ''}>{s.title}</a>
          ))}
        </nav>

        <div className="cs-concept-main cs-plan">
          {header.intro && (
            <section className="cs-plan-section cs-concept-intro">
              <MarkdownLite text={header.intro} />
            </section>
          )}

          <nav className="cs-plan-toc cs-concept-toc-inline">
            {sections.map((s) => (
              <a key={s.id} href={`#${s.id}`}>{s.title}</a>
            ))}
          </nav>

          {sections.map((s) => (
            <section key={s.id} id={s.id} className="cs-plan-section">
              <h2>{s.title}</h2>
              <MarkdownFigures text={s.body} focus={focus} screens={def.screens} />
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
